import { Id } from "@dgs/core";
import { TFunction } from "i18next";
import { ReactNode } from "react";
import { Address } from "../../ui/Address";
import {
	ContactAttributeRequest,
	IContactAttributeBaseHandler,
	IContactAttributeHandler,
	IContactAttributeListHandler,
} from "../IContactAttributeHandler";
import { AddressFormFields, BaseAddressValue, validateAddress } from "./AddressFormFields";

type PrimaryAddressValue = BaseAddressValue;

interface PrimaryAddressSaveRequest extends Omit<PrimaryAddressValue, "country"> {
	countryId: Id;
}

export function createPrimaryAddressContactAttributeBaseHandler(id: Id): IContactAttributeBaseHandler {
	return {
		type: "PRIMARY_ADDRESS",
		id,
		getLabel: (t) => t("Primary address"),
	};
}

export function createPrimaryAddressContactAttributeProfileHandler(
	id: Id,
	value: PrimaryAddressValue | null,
): IContactAttributeHandler<PrimaryAddressSaveRequest, PrimaryAddressValue | null> {
	const baseHandler = createPrimaryAddressContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		value,
		validate(t, value: PrimaryAddressValue | null) {
			return validateAddress(t, value);
		},
		renderValue(): ReactNode {
			if (!value) return "-";
			return <Address address={value} />;
		},
		renderFormField(t: TFunction, prefix): ReactNode {
			const name = `${prefix ? `${prefix}.` : ""}${id}.value`;
			return <AddressFormFields name={name} />;
		},
		toRequestValue(): ContactAttributeRequest<PrimaryAddressSaveRequest> {
			if (this.value === null) {
				throw new Error("Primary address must not be null");
			}

			return { id, value: { ...this.value, countryId: this.value.country?.id as Id } };
		},
	};
}

export function createPrimaryAddressContactAttributeListHandler(id: Id): IContactAttributeListHandler {
	const baseHandler = createPrimaryAddressContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		getColumnSize: () => "1fr",
	};
}
